import React, { useEffect, useState } from "react";
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { api } from "../../api/apiService";

type Piece = { id: string; title: string; summary: string };
type Pub = { id: string; kind: string; destination: string; status: string; detail?: string };
type AudienceAccount = { id: string; provider: string; profile_id: string; scope: string; list_id: string; sender_email: string; sender_name: string };
type View = { piece: Piece; publishes: Pub[] };

const darkPanelSx = {
  color: "#fafafa",
  "& .MuiTypography-root": { color: "inherit" },
  "& .MuiTypography-colorTextSecondary": { color: "#a1a1aa" },
  "& .MuiInputLabel-root": { color: "#a1a1aa" },
  "& .MuiFormHelperText-root": { color: "#71717a" },
  "& .MuiOutlinedInput-root": {
    color: "#fafafa",
    "& fieldset": { borderColor: "#3f3f46" },
    "&:hover fieldset": { borderColor: "#71717a" },
  },
  "& .MuiSelect-icon": { color: "#a1a1aa" },
};

function errorText(err: unknown): string {
  const anyErr = err as { data?: unknown; response?: { data?: unknown }; message?: string };
  const data = anyErr?.data ?? anyErr?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  return anyErr?.message || "Request failed";
}

/** Sends the open article to a CMS venue or a saved mail list. */
export function ArticlePublishPanel({ notebookId, pieceTitle }: { notebookId: string; pieceTitle?: string }) {
  const [audiences, setAudiences] = useState<AudienceAccount[]>([]);
  const [view, setView] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [publishKind, setPublishKind] = useState("portal");
  const [destination, setDestination] = useState("");
  const [mailProvider, setMailProvider] = useState("brevo");
  const [mailProfile, setMailProfile] = useState("");
  const [mailToken, setMailToken] = useState("");
  const [mailList, setMailList] = useState("");
  const [mailSender, setMailSender] = useState("");
  const [mailSenderName, setMailSenderName] = useState("");

  const openPiece = async (id: string) => setView(await api.get("/api/desk/pieces/" + id));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = ((await api.get("/api/desk/pieces")) || []) as Piece[];
        if (cancelled) return;
        setAudiences(((await api.get("/api/desk/audience-accounts")) || []) as AudienceAccount[]);
        const marker = "nb:" + notebookId;
        let piece = list.find((item) => item.summary === marker);
        if (!piece) {
          piece = await api.post("/api/desk/pieces", {
            title: (pieceTitle || "").trim() || "Untitled",
            summary: marker,
          });
        }
        if (!cancelled && piece) setView(await api.get("/api/desk/pieces/" + piece.id));
      } catch (err) {
        if (!cancelled) setError(errorText(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [notebookId, pieceTitle]);

  const run = async (fn: () => Promise<void>) => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errorText(err));
    }
  };

  if (!view) {
    return error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null;
  }

  return (
    <Box sx={{ ...darkPanelSx, pt: 2, maxWidth: 960 }}>
      <Typography variant="h6" gutterBottom>Send this article</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Stack spacing={3}>
        <Typography variant="subtitle1">Publish</Typography>
        {view.publishes.map((row) => (
          <Typography key={row.id} variant="body2">{row.kind} · {row.status} · {row.destination} {row.detail || ""}</Typography>
        ))}
        <Typography variant="body2" color="text.secondary">
          Brevo and Mailchimp accounts are saved for the company when the analytic profile id is blank, or for that profile when it is set. The token is not shown again. A Mailchimp key ends with its data center, such as -us21. A Brevo list id is a number.
        </Typography>
        {audiences.map((account) => (
          <Typography key={account.id} variant="body2">
            {account.provider} · {account.scope} · list {account.list_id} · {account.sender_email}
          </Typography>
        ))}
        <Stack direction="row" spacing={1}>
          <TextField select label="Mail provider" value={mailProvider} onChange={(e) => setMailProvider(e.target.value)} size="small">
            <MenuItem value="brevo">brevo</MenuItem>
            <MenuItem value="mailchimp">mailchimp</MenuItem>
          </TextField>
          <TextField label="Analytic profile id" value={mailProfile} onChange={(e) => setMailProfile(e.target.value)} size="small" placeholder="blank for company" />
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField label="API token" type="password" value={mailToken} onChange={(e) => setMailToken(e.target.value)} size="small" />
          <TextField label="List or audience id" value={mailList} onChange={(e) => setMailList(e.target.value)} size="small" />
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField label="Sender name" value={mailSenderName} onChange={(e) => setMailSenderName(e.target.value)} size="small" />
          <TextField label="Sender email" value={mailSender} onChange={(e) => setMailSender(e.target.value)} size="small" />
          <Button variant="outlined" onClick={() => run(async () => {
            await api.put("/api/desk/audience-accounts", {
              provider: mailProvider,
              profile_id: mailProfile,
              token: mailToken,
              list_id: mailList,
              sender_email: mailSender,
              sender_name: mailSenderName,
            });
            setMailToken("");
            setAudiences(((await api.get("/api/desk/audience-accounts")) || []) as AudienceAccount[]);
          })}>Save mail account</Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField select label="Destination" value={publishKind} onChange={(e) => setPublishKind(e.target.value)} size="small">
            <MenuItem value="portal">portal venue</MenuItem>
            <MenuItem value="wordpress">wordpress venue</MenuItem>
            <MenuItem value="arc_xp">arc xp venue</MenuItem>
            <MenuItem value="blox">blox venue</MenuItem>
            <MenuItem value="brightspot">brightspot venue</MenuItem>
            <MenuItem value="email">brevo or mailchimp list</MenuItem>
          </TextField>
          {publishKind !== "email" && (
            <TextField label="Venue id" value={destination} onChange={(e) => setDestination(e.target.value)} size="small" />
          )}
        </Stack>
        <Button variant="contained" sx={{ alignSelf: "flex-start" }} onClick={() => run(async () => {
          await api.post("/api/desk/pieces/" + view.piece.id + "/publish", publishKind === "email"
            ? { kind: "email", provider: mailProvider, profile_id: mailProfile }
            : { kind: publishKind, venue_id: destination, destination: "" });
          await openPiece(view.piece.id);
        })}>Publish approved article</Button>
      </Stack>
    </Box>
  );
}
